import { FullscreenModal } from '@round/whitelabel-ui';
import { Modal } from '@round/whitelabel-ui';
import { useProjectSelect } from './useProjectSelect';
import ProjectSelectTable from './ProjectSelectTable/ProjectSelectTable';
import styles from './ProjectSelectModal.module.css';
import { useEffect, useState } from 'react';
import { RowSelectionState } from '@tanstack/react-table';
import { Button } from '@round/whitelabel-ui';
import { showNotification } from 'helpers';
import { useRefreshReportProjects } from '../hooks/useRefreshReportProjects';
import { useProjectSelectForm } from './useProjectSelectForm';
import { SearchInput } from '@round/whitelabel-ui';
import { Checkbox } from '@round/whitelabel-ui';
import { SkeletonizedValue } from '@round/ui-kit';
import { creatorbase } from '@round/api';
import { isNumber } from 'utility/utility';
import { useReport } from '@round/creatorbase-helpers/api-hooks';
import useReportId from '../hooks/useReportId';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (report: creatorbase.Report) => void;
};

const ProjectSelectModal = ({ isOpen, onClose, onSuccess }: Props) => {
    const [search, setSearch] = useState('');
    const [isShowSelectedOnlyActive, setIsShowSelectedOnlyActive] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSearch('');
            setIsShowSelectedOnlyActive(false);
        }
    }, [isOpen]);

    const { data: reportData, errorMessage: reportErrorMessage } = useReport(useReportId());
    const refreshReportData = useRefreshReportProjects();
    const { rows, meta, hasNextPage, fetchNextPage } = useProjectSelect(
        { search, accessible_by_team_id: reportData?.team_id },
        { enabled: isOpen && isNumber(reportData?.team_id) }
    );

    const {
        formik,
        isLoading: isFormLoading,
        isError: isFormError,
    } = useProjectSelectForm({
        onSubmitSuccess: (report) => {
            showNotification('Projects updated', 'info');
            onSuccess(report);
            refreshReportData();
        },
        onSubmitError: () => {
            showNotification('Failed to update projects', 'error');
        },
        isFormActive: isOpen,
    });

    const isFormInitialized = !isFormLoading;

    const isLoading = meta.isLoading || isFormLoading;
    const hasError = meta.hasError || !!reportErrorMessage || isFormError;

    const selectedProjectCount = formik.values.projects.length;
    const selectedProjectsLabel = `${selectedProjectCount ? selectedProjectCount : 'No'} ${
        selectedProjectCount === 1 ? 'project' : 'projects'
    } selected`;

    return (
        <FullscreenModal className={styles.modal} isOpen={isOpen} onClose={onClose} closeOnEscape closeOnOverlayClick>
            <Modal.Header>
                <Modal.Title>Edit projects</Modal.Title>
            </Modal.Header>

            <div className={styles.menuContainer}>
                <menu className={styles.filterMenu}>
                    <SearchInput
                        placeholder="Search projects"
                        value={search}
                        onChange={(val) => {
                            setSearch(val);
                        }}
                        className={styles.searchInput}
                    />

                    <div className={styles.selectedOnlyFilter}>
                        <Checkbox
                            value={isShowSelectedOnlyActive}
                            onChange={() => {
                                setIsShowSelectedOnlyActive((prev) => !prev);
                            }}
                        />
                        <label
                            onClick={() => {
                                setIsShowSelectedOnlyActive((prev) => !prev);
                            }}
                        >
                            Show selected only
                        </label>
                    </div>
                </menu>

                <SkeletonizedValue isInitialized={isFormInitialized} width={'10rem'}>
                    <p className={styles.countLabel}>{selectedProjectsLabel}</p>
                </SkeletonizedValue>
            </div>

            <Modal.Body>
                <ProjectSelectTable
                    containerClassName={styles.table}
                    data={isShowSelectedOnlyActive ? formik.values.projects : rows}
                    meta={{
                        ...meta,
                        isLoading: isShowSelectedOnlyActive ? false : isLoading,
                        isFetchingNextPage: isShowSelectedOnlyActive ? false : meta.isFetchingNextPage,
                        hasError,
                    }}
                    onEndReached={() => {
                        if (!isShowSelectedOnlyActive && hasNextPage && !meta.isFetchingNextPage) {
                            void fetchNextPage();
                        }
                    }}
                    rowSelection={formik.values.projects.reduce((acc, project) => {
                        acc[`${project.id}`] = true;
                        return acc;
                    }, {} as RowSelectionState)}
                    onRowSelectionChange={(selection) => {
                        const persistingSelectedProjects = formik.values.projects.filter(
                            (project) => selection[`${project.id}`]
                        );

                        const newProjects = rows.filter(
                            (row) =>
                                selection[`${row.id}`] &&
                                !persistingSelectedProjects.find((project) => project.id === row.id)
                        );

                        formik.setFieldValue('projects', [...persistingSelectedProjects, ...(newProjects ?? [])]);
                    }}
                />
            </Modal.Body>
            <Modal.Actions>
                <Button
                    appearance="primary"
                    onClick={() => formik.handleSubmit()}
                    isLoading={formik.isSubmitting}
                    disabled={formik.isSubmitting || !formik.dirty || hasError}
                >
                    Save
                </Button>

                <Button appearance="ghost" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Actions>
        </FullscreenModal>
    );
};

export default ProjectSelectModal;
